import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { RefObject, useState } from 'react';

import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Input from '@cartwhl/ui/atoms/Input';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';
import MinusIcon from '@cartwhl/ui/icons/MinusIcon';
import SearchIcon from '@cartwhl/ui/icons/SearchIcon';
import SortIcon from '@cartwhl/ui/icons/SortIcon';
import Button from '@cartwhl/ui/input/Button';

import { AssignableBone } from '../types';
import { RetargetingBone } from '../types';
import AssignedIcon from './AssignedIcon';

export default function RetargetingBonesList({
  stepBones,
  assignableBones,
  onAssign,
  onUnassign,
  stageRef,
  setHighlightBone,
}: {
  stepBones: RetargetingBone[];
  assignableBones: AssignableBone[];
  onAssign: (cartwheelBoneKey: string, boneToAssign: THREE.Bone) => void;
  onUnassign: (bone: THREE.Bone) => void;
  stageRef: RefObject<HTMLDivElement | null>;
  setHighlightBone: (boneKey: string, highlight: boolean) => void;
}) {
  const [filter, setFilter] = useState<'all' | 'unassigned'>('all');
  return (
    <div className="bg-gray-15 border-gray-30 dark:bg-gray-75 dark:border-gray-60 mt-1 flex min-h-0 flex-col rounded-lg border">
      <div className="border-gray-30 dark:border-gray-60 flex items-center justify-between border-b p-2">
        <Text as="h3" variant="studio/body/medium/sm">
          Cartwheel bones list
        </Text>

        <Dropdown
          trigger={
            <Button variant="secondary" className="-my-0.5" size="sm">
              <SortIcon />
            </Button>
          }
        >
          <Text
            as="div"
            variant="studio/body/medium/sm"
            className="text-gray-60 dark:text-gray-45 px-3 py-2"
          >
            Filter by
          </Text>
          <Dropdown.Item
            onClick={() => setFilter('all')}
            className={
              filter === 'all' ? 'bg-blue hover:bg-blue text-white' : ''
            }
          >
            All
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => setFilter('unassigned')}
            className={
              filter === 'unassigned' ? 'bg-blue hover:bg-blue text-white' : ''
            }
          >
            Unassigned
          </Dropdown.Item>
        </Dropdown>
      </div>
      <div className="no-scrollbar flex flex-col gap-1 overflow-y-scroll p-2">
        {filter === 'unassigned' && (
          <Text
            as="div"
            variant="studio/body/sm"
            className="text-gray-60 dark:text-gray-45 mb-1"
          >
            Filtered by {filter}
          </Text>
        )}
        {stepBones
          .filter((bone) => {
            if (filter === 'all') return true;
            return !bone.assignedBone;
          })
          .map((item) => (
            <AssignableBoneItem
              key={item.displayName}
              bone={item}
              assignableBones={assignableBones}
              onAssign={onAssign}
              onUnassign={onUnassign}
              stageRef={stageRef}
              setHighlightBone={setHighlightBone}
            />
          ))}
      </div>
    </div>
  );
}

function AssignableBoneItem({
  bone,
  assignableBones,
  stageRef,
  onAssign,
  onUnassign,
  setHighlightBone,
}: {
  bone: RetargetingBone;
  assignableBones: AssignableBone[];
  stageRef: RefObject<HTMLDivElement | null>;
  onAssign: (cartwheelBoneKey: string, boneToAssign: THREE.Bone) => void;
  onUnassign: (bone: THREE.Bone) => void;
  setHighlightBone: (boneKey: string, highlight: boolean) => void;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const isAssigned = !!bone.assignedBone;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className="hover:bg-gray-30 dark:hover:bg-gray-60 dark:bg-gray-75 data-[state=open]:bg-gray-30 dark:data-[state=open]:bg-gray-60 group relative flex h-10 flex-none cursor-pointer items-center gap-2 rounded-lg bg-white px-2 transition-colors"
          onMouseEnter={() =>
            bone.assignedBone && setHighlightBone(bone.assignedBone.name, true)
          }
          onMouseLeave={() =>
            bone.assignedBone && setHighlightBone(bone.assignedBone.name, false)
          }
        >
          {isAssigned ? (
            <AssignedIcon className="flex-none text-green-50" />
          ) : (
            <UnassignedIcon className="text-gray-60 dark:text-gray-45 flex-none" />
          )}
          <div className="flex flex-col">
            <Text
              as="div"
              variant="studio/body/medium/sm"
              className="text-left text-[14px]/[14px]"
            >
              {bone.displayName}
            </Text>
            {bone.assignedBone && (
              <Text
                as="div"
                variant="studio/body/sm"
                className="text-gray-60 dark:text-gray-45 text-[12px]/[12px]"
              >
                {bone.assignedBone.userData.originalName ||
                  bone.cartwheelBoneKey}
              </Text>
            )}
          </div>
          {!isAssigned && (
            <div className="ml-auto flex items-center justify-center gap-2">
              <Text
                variant="studio/body/sm"
                className="text-gray-60 dark:text-gray-45 opacity-0 transition-opacity group-hover:opacity-100"
              >
                Assign
              </Text>

              <ChevronRightIcon className="text-gray-60 dark:text-gray-45 flex-none" />
            </div>
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          side="right"
          sideOffset={32}
          collisionBoundary={stageRef.current}
          collisionPadding={16}
          className="retargetingSubmenuEntry dark:bg-gray-75 z-50 flex h-[450px] min-w-[252px] flex-col rounded-lg bg-white"
        >
          <div className="border-gray-30 dark:border-gray-60 flex justify-between border-b py-3 pr-2 pl-4">
            <div className="flex flex-col">
              <Text as="div" variant="studio/body/medium/sm">
                <span className="text-gray-60 dark:text-gray-45">Assign</span> "
                {bone.displayName}"
              </Text>

              <Text
                as="div"
                variant="studio/body/sm"
                className="text-gray-60 dark:text-gray-45 font-normal"
              >
                {assignableBones.length} bones —{' '}
                {assignableBones.filter((bone) => !bone.assignedTo).length}{' '}
                unassigned
              </Text>
            </div>
            <div>
              {bone.assignedBone && (
                <Tooltip text="Unassign bone" inModal side="right">
                  <Button
                    size="sm"
                    icon={
                      <MinusIcon className="text-gray-60 dark:text-gray-45 ml-0.5 h-4 w-4" />
                    }
                    variant="secondary"
                    onClick={() =>
                      bone.assignedBone && onUnassign(bone.assignedBone)
                    }
                  />
                </Tooltip>
              )}
            </div>
          </div>
          <div className="relative mx-2 mt-2">
            <SearchIcon className="text-gray-60 dark:text-gray-45 absolute top-1/2 left-2 flex-none -translate-y-1/2" />
            <Input
              name="search-bone"
              placeholder="Search bone"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8"
            />
          </div>
          <div className="no-scrollbar relative flex flex-col overflow-y-scroll p-2">
            {assignableBones
              .filter((bone) =>
                bone.bone.userData.originalName
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase()),
              )
              .sort(isUnassignedFirst)
              .sort(isCurrentBoneFirst(bone.cartwheelBoneKey))
              .map((item) => {
                const isActive = item.assignedTo === bone.cartwheelBoneKey;
                const isAssigned = item.assignedTo !== null;
                return (
                  <button
                    key={item.bone.name}
                    className={`group disabled:text-opacity-50 flex h-10 flex-none items-center gap-2 rounded-lg px-2 ${
                      isAssigned
                        ? ''
                        : 'hover:bg-gray-15 dark:hover:bg-gray-60 cursor-pointer'
                    }`}
                    disabled={isAssigned}
                    onClick={(e) => {
                      e.preventDefault();
                      onAssign(bone.cartwheelBoneKey, item.bone);
                    }}
                    onMouseEnter={() => setHighlightBone(item.bone.name, true)}
                    onMouseLeave={() => setHighlightBone(item.bone.name, false)}
                  >
                    {isActive ? (
                      <AssignedIcon className="flex-none text-green-50" />
                    ) : (
                      <span className="w-5 flex-none" />
                    )}
                    <Text
                      as="div"
                      variant={
                        isAssigned ? 'studio/body/medium/sm' : 'studio/body/sm'
                      }
                      className={`flex flex-1 justify-between ${isAssigned ? 'text-gray-60 dark:text-gray-45' : ''}`}
                    >
                      <span>{item.bone.userData.originalName}</span>
                    </Text>
                  </button>
                );
              })}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

const isUnassignedFirst = (a: AssignableBone, b: AssignableBone) => {
  if (a.assignedTo === null && b.assignedTo !== null) return -1;
  if (a.assignedTo !== null && b.assignedTo === null) return 1;
  return 0;
};

const isCurrentBoneFirst =
  (currentBoneKey: string) => (a: AssignableBone, b: AssignableBone) => {
    if (a.bone.name === currentBoneKey) return -1;
    if (b.bone.name === currentBoneKey) return 1;
    return 0;
  };

const UnassignedIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path d="M9.99365 16.9741C9.09652 16.9741 8.25228 16.8027 7.46094 16.46C6.6696 16.1214 5.97135 15.6496 5.36621 15.0444C4.7653 14.4393 4.29346 13.741 3.95068 12.9497C3.60791 12.1584 3.43652 11.3141 3.43652 10.417C3.43652 9.51986 3.60791 8.67562 3.95068 7.88428C4.29346 7.09294 4.7653 6.39681 5.36621 5.7959C5.97135 5.19076 6.66748 4.7168 7.45459 4.37402C8.24593 4.03125 9.09017 3.85986 9.9873 3.85986C10.8887 3.85986 11.735 4.03125 12.5264 4.37402C13.3177 4.7168 14.016 5.19076 14.6211 5.7959C15.2262 6.39681 15.7002 7.09294 16.043 7.88428C16.3857 8.67562 16.5571 9.51986 16.5571 10.417C16.5571 11.3141 16.3857 12.1584 16.043 12.9497C15.7002 13.741 15.2262 14.4393 14.6211 15.0444C14.016 15.6496 13.3177 16.1214 12.5264 16.46C11.735 16.8027 10.8908 16.9741 9.99365 16.9741ZM9.99365 15.6792C10.7257 15.6792 11.4092 15.5438 12.0439 15.2729C12.6787 15.0021 13.2373 14.6255 13.7197 14.1431C14.2021 13.6606 14.5788 13.1021 14.8496 12.4673C15.1204 11.8325 15.2559 11.1491 15.2559 10.417C15.2559 9.68913 15.1204 9.00781 14.8496 8.37305C14.5788 7.73405 14.2021 7.17334 13.7197 6.69092C13.2373 6.2085 12.6766 5.83187 12.0376 5.56104C11.4028 5.2902 10.7194 5.15479 9.9873 5.15479C9.25944 5.15479 8.57601 5.2902 7.93701 5.56104C7.30225 5.83187 6.74577 6.2085 6.26758 6.69092C5.78939 7.17334 5.41488 7.73405 5.14404 8.37305C4.87321 9.00781 4.73779 9.68913 4.73779 10.417C4.73779 11.1491 4.87321 11.8325 5.14404 12.4673C5.41488 13.1021 5.78939 13.6606 6.26758 14.1431C6.75 14.6255 7.30859 15.0021 7.94336 15.2729C8.58236 15.5438 9.26579 15.6792 9.99365 15.6792Z" />
    </svg>
  );
};
